import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ShopLogo, ShopName } from '../components/ShopLogo';
import { getWebsiteConfig, WebsiteConfig, DEFAULT_CONFIG } from '../services/websiteConfigService';
import { visibleBarbers } from '../shared/barberStatus';
import { publicHoursService } from '../services/publicHoursService';
import {
  Scissors,
  Clock,
  MapPin,
  Phone,
  Star,
  ChevronRight,
  Instagram,
  Facebook,
  Calendar,
  Users,
  Award,
  Sparkles,
  X,
  DollarSign,
} from 'lucide-react';

// Types
interface Barber {
  id: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  photo?: string;      // real stored field (avatar was legacy/empty)
  status?: string;     // POS archive status — needed for visibleBarbers()
  isActive?: boolean;
  bio?: string;
  specialties?: string[];
  rating?: number;
}

interface Service {
  id: string;
  name: string;
  price: number;
  duration: number;
  imageUrl?: string;
  description?: string;
  category?: string;
}

// Service Detail Modal
const ServiceModal: React.FC<{
  service: Service | null;
  onClose: () => void;
  onBook: (serviceId: string) => void;
}> = ({ service, onClose, onBook }) => {
  if (!service) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl animate-scale-in"
        onClick={e => e.stopPropagation()}
      >
        {/* Image/Header */}
        <div className="h-48 bg-gradient-to-br from-slik-purple/20 to-slik-magenta/20 flex items-center justify-center relative">
          {service.imageUrl ? (
            <img src={service.imageUrl} alt={service.name} className="w-full h-full object-cover" />
          ) : (
            <Scissors className="w-20 h-20 text-slik-purple/40" />
          )}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {service.category && (
            <span className="text-xs font-medium text-slik-purple bg-slik-purple/10 px-2 py-1 rounded-full">
              {service.category}
            </span>
          )}
          <h2 className="text-2xl font-bold text-slik-navy mt-2 mb-2">{service.name}</h2>

          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-1 text-gray-500">
              <Clock className="w-4 h-4" />
              <span className="text-sm">{service.duration} min</span>
            </div>
            <div className="flex items-center gap-1 text-slik-purple font-bold">
              <DollarSign className="w-4 h-4" />
              <span className="text-xl">{service.price}</span>
            </div>
          </div>

          {service.description ? (
            <p className="text-gray-600 mb-6">{service.description}</p>
          ) : (
            <p className="text-gray-600 mb-6">
              Professional {service.name.toLowerCase()} service performed by our skilled barbers.
              Includes consultation and styling advice.
            </p>
          )}

          <button
            onClick={() => onBook(service.id)}
            className="w-full bg-slik-purple text-white py-4 rounded-button font-bold shadow-button hover:opacity-90 transition-all flex items-center justify-center gap-2"
          >
            <Calendar className="w-5 h-5" />
            Book This Service
          </button>
        </div>
      </div>
    </div>
  );
};

// Barber Profile Modal
const BarberModal: React.FC<{
  barber: Barber | null;
  onClose: () => void;
  onBook: (barberId: string) => void;
}> = ({ barber, onClose, onBook }) => {
  if (!barber) return null;

  const name = `${barber.firstName} ${barber.lastName}`.trim() || 'Barber';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl animate-scale-in"
        onClick={e => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className="h-32 bg-gradient-to-br from-slik-purple to-slik-magenta relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Avatar - overlapping */}
        <div className="flex justify-center -mt-16 relative z-10">
          {(barber.photo || barber.avatar) ? (
            <img
              src={barber.photo || barber.avatar}
              alt={name}
              className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
            />
          ) : (
            <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg bg-gradient-to-br from-slik-purple to-slik-magenta flex items-center justify-center text-white text-4xl font-bold">
              {barber.firstName?.charAt(0) || 'B'}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <h2 className="text-2xl font-bold text-slik-navy mb-1">{name}</h2>

          <div className="flex items-center justify-center gap-1 text-yellow-500 mb-4">
            <Star className="w-5 h-5 fill-yellow-500" />
            <span className="font-bold">{barber.rating?.toFixed(1) || '4.9'}</span>
            <span className="text-gray-400 text-sm">(50+ reviews)</span>
          </div>

          {barber.specialties && barber.specialties.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center mb-4">
              {barber.specialties.map((specialty, idx) => (
                <span
                  key={idx}
                  className="text-xs font-medium text-slik-purple bg-slik-purple/10 px-3 py-1 rounded-full"
                >
                  {specialty}
                </span>
              ))}
            </div>
          )}

          {barber.bio ? (
            <p className="text-gray-600 mb-6">{barber.bio}</p>
          ) : (
            <p className="text-gray-600 mb-6">
              Experienced barber specializing in modern and classic cuts.
              Dedicated to making every client look and feel their best.
            </p>
          )}

          <button
            onClick={() => onBook(barber.id)}
            className="w-full bg-slik-purple text-white py-4 rounded-button font-bold shadow-button hover:opacity-90 transition-all flex items-center justify-center gap-2"
          >
            <Calendar className="w-5 h-5" />
            Book with {barber.firstName || 'This Barber'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ShopInfo interface removed - using WebsiteConfig from service

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [barbers, setBarbers] = useState<Barber[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<WebsiteConfig>(DEFAULT_CONFIG);
  const [hoursLines, setHoursLines] = useState<{ label: string; value: string }[]>([]);

  // Modal state
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedBarber, setSelectedBarber] = useState<Barber | null>(null);

  useEffect(() => {
    loadData();
    loadConfig();
  }, []);

  const loadConfig = async () => {
    const websiteConfig = await getWebsiteConfig();
    setConfig(websiteConfig);
    // Footer hours = owner's real per-day schedule (Settings → Hours & Schedule), the SAME
    // source that bounds booking — not a hardcoded list. See publicHoursService.
    setHoursLines(await publicHoursService.getFormattedHours());
  };

  // Handle booking navigation with pre-selection
  const handleBookService = (serviceId: string) => {
    setSelectedService(null);
    navigate(`/book?serviceId=${serviceId}`);
  };

  const handleBookBarber = (barberId: string) => {
    setSelectedBarber(null);
    navigate(`/book?barberId=${barberId}`);
  };

  const loadData = async () => {
    try {
      // Website config (POS Settings → Website) decides WHICH barbers/services are featured.
      const cfg = await getWebsiteConfig();

      // Load barbers
      const barbersSnapshot = await getDocs(collection(db, 'barbers'));
      const barbersData = barbersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          avatar: data.avatar,
          photo: data.photo,        // real stored photo URL
          status: data.status,      // fed to visibleBarbers() so archived hide
          isActive: data.isActive,  // legacy active flag (honored by shared rule)
          bio: data.bio,
          specialties: data.specialties,
          rating: data.rating || 4.9,
        } as Barber;
      });
      // BRAIN #4: hide inactive/archived (James/Eric) + apprentices.
      // Then: if the POS picked Featured Barbers, show exactly those (order preserved);
      // else fall back to the first few visible barbers.
      const visible = visibleBarbers(barbersData as any) as Barber[];
      const featuredBarbers = cfg.featuredBarberIds?.length
        ? (cfg.featuredBarberIds.map(id => visible.find(b => b.id === id)).filter(Boolean) as Barber[])
        : visible;
      setBarbers(featuredBarbers.slice(0, 4));

      // Load services from service_categories — build the FULL variation list so Featured
      // Services (POS Settings) can pick ANY service, not just the first per category.
      const categoriesSnapshot = await getDocs(collection(db, 'service_categories'));
      const allVariations: Service[] = [];
      const diversified: Service[] = []; // fallback: first 2 flat / 1 sub per category

      categoriesSnapshot.docs.forEach(doc => {
        const category = doc.data();
        const toService = (v: any): Service => ({
          id: v.id,
          name: v.name,
          price: v.price,
          duration: v.duration,
          imageUrl: v.imageUrl,
          description: v.description,
          category: category.name,
        });
        if (category.isFlat && category.variations) {
          category.variations.forEach((v: any, i: number) => {
            const s = toService(v); allVariations.push(s); if (i < 2) diversified.push(s);
          });
        } else if (category.subcategories) {
          category.subcategories.forEach((sub: any) => {
            sub.variations?.forEach((v: any, i: number) => {
              const s = toService(v); allVariations.push(s); if (i < 1) diversified.push(s);
            });
          });
        }
      });

      // Featured selection wins (so images/curation are intentional); else diversified fallback.
      const featuredServices = cfg.featuredServiceIds?.length
        ? (cfg.featuredServiceIds.map(id => allVariations.find(s => s.id === id)).filter(Boolean) as Service[])
        : diversified;
      setServices(featuredServices.slice(0, 6)); // Show max 6 services
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBarberName = (barber: Barber) => {
    return `${barber.firstName} ${barber.lastName}`.trim() || 'Barber';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <ShopLogo className="w-10 h-10 object-contain rounded-xl" />
              <ShopName nameClassName="text-xl font-bold text-slik-navy" taglineClassName="text-[11px] text-gray-500 hidden sm:block" />
            </div>

            {/* Nav Links - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-gray-600 hover:text-slik-purple transition-colors">Services</a>
              <a href="#barbers" className="text-gray-600 hover:text-slik-purple transition-colors">Our Team</a>
              <a href="#reviews" className="text-gray-600 hover:text-slik-purple transition-colors">Reviews</a>
              <a href="#contact" className="text-gray-600 hover:text-slik-purple transition-colors">Contact</a>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-3">
              {/* Social icons (copy of footer) — top-right, cosmetic per Jose 2026-09-19. SEO already covered by sameAs schema. */}
              <a href={config.socialLinks?.instagram || "https://www.instagram.com/slik_barber_lounge"} target="_blank" rel="noopener noreferrer" aria-label="SLIK Barber Lounge on Instagram" className="hidden sm:inline-flex text-gray-500 hover:text-slik-purple transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href={config.socialLinks?.facebook || "https://www.facebook.com/SLIKBarberLounge/"} target="_blank" rel="noopener noreferrer" aria-label="SLIK Barber Lounge on Facebook" className="hidden sm:inline-flex text-gray-500 hover:text-slik-purple transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <Link
                to="/login"
                className="text-slik-purple font-medium hover:text-slik-magenta transition-colors text-sm sm:text-base"
              >
                Sign In
              </Link>
              <Link
                to="/book"
                className="bg-slik-purple text-white px-5 py-2 rounded-button font-bold shadow-button hover:opacity-90 transition-all"
              >
                Book Now
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={config.hero.imageUrl}
            alt="Barbershop"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slik-navy/90 via-slik-navy/70 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl animate-slide-up">
            {/* Hero headline = the tagline only. The "Florham Park's Barber Lounge" line was removed
                per Jose 2026-09-18 (it changed the look). Local-SEO "Florham Park" lives in the meta
                title + the SEO landing pages, not the hero. */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {/* Lightened per Jose 2026-09-18. `bg-slik-purple` clips the global charcoal→copper
                  gradient (linear-gradient 135deg #2A211A→#C0824A), so the left half of the text was
                  near-black on the dark hero. Explicit LIGHT copper gradient instead — readable. */}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-copper-200 via-copper-300 to-copper-400">
                Skin Fades &amp; Exceptional Style
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-lg">
              {config.shopInfo.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/book"
                className="inline-flex items-center justify-center gap-2 bg-slik-purple text-white px-8 py-4 rounded-button font-bold text-lg shadow-button hover:opacity-90 transition-all group"
              >
                <Calendar className="w-5 h-5" />
                Book Appointment
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href={`tel:${config.shopInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-button font-bold text-lg border border-white/20 hover:bg-white/20 transition-all"
              >
                <Phone className="w-5 h-5" />
                Call Us
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex items-center gap-6 mt-10">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-slik-purple to-slik-magenta border-2 border-white" />
                  ))}
                </div>
                <span className="text-white/80 text-sm">{config.stats.happyClients} Happy Clients</span>
              </div>
              <div className="flex items-center gap-1">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span className="text-white font-bold">{config.stats.rating}</span>
                <span className="text-white/60 text-sm">({config.stats.reviewCount} reviews)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-slik-navy py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Users, value: config.stats.happyClients, label: "Happy Clients" },
              { icon: Scissors, value: config.stats.haircutsDone, label: "Haircuts Done" },
              { icon: Award, value: config.stats.yearsExperience, label: "Years Experience" },
              { icon: Star, value: config.stats.rating, label: "Average Rating" },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <stat.icon className="w-8 h-8 text-slik-purple mx-auto mb-2" />
                <p className="text-3xl font-bold text-white">{stat.value}</p>
                <p className="text-gray-400 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slik-navy mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From classic cuts to modern styles, we offer a full range of barbering services tailored to your needs.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                  <div className="w-full h-40 bg-gray-200 rounded-xl mb-4" />
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, idx) => (
                <button
                  key={service.id}
                  onClick={() => setSelectedService(service)}
                  className="bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover transition-all group text-left"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className="h-40 bg-gradient-to-br from-slik-purple/10 to-slik-magenta/10 flex items-center justify-center relative overflow-hidden">
                    {service.imageUrl ? (
                      <img src={service.imageUrl} alt={service.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <Scissors className="w-16 h-16 text-slik-purple/30 group-hover:scale-110 transition-transform" />
                    )}
                    <div className="absolute inset-0 bg-slik-purple/0 group-hover:bg-slik-purple/10 transition-colors flex items-center justify-center">
                      <span className="text-white font-bold opacity-0 group-hover:opacity-100 transition-opacity bg-slik-purple/80 px-4 py-2 rounded-full text-sm">
                        View Details
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-slik-navy mb-2 group-hover:text-slik-purple transition-colors">{service.name}</h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-gray-500">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{service.duration} min</span>
                      </div>
                      <span className="text-2xl font-bold text-slik-purple">${service.price}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="text-center mt-10">
            <Link
              to="/book"
              className="inline-flex items-center gap-2 bg-slik-purple text-white px-8 py-3 rounded-button font-bold shadow-button hover:bg-slik-purple/90 transition-all"
            >
              View All Services & Book
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Barbers Section */}
      <section id="barbers" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slik-navy mb-4">Meet Our Team</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Skilled professionals dedicated to giving you the perfect look every time.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="text-center animate-pulse">
                  <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-4" />
                  <div className="h-6 bg-gray-200 rounded w-24 mx-auto mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-20 mx-auto" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {barbers.map((barber, idx) => (
                <button
                  key={barber.id}
                  onClick={() => setSelectedBarber(barber)}
                  className="text-center group cursor-pointer"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className="relative w-40 h-40 mx-auto mb-4">
                    <div className="absolute inset-0 bg-gradient-to-br from-slik-purple to-slik-magenta rounded-full opacity-0 group-hover:opacity-100 transition-opacity scale-105" />
                    {(barber.photo || barber.avatar) ? (
                      <img
                        src={barber.photo || barber.avatar}
                        alt={getBarberName(barber)}
                        className="relative w-full h-full object-cover rounded-full border-4 border-white shadow-lg group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="relative w-full h-full bg-gradient-to-br from-slik-purple to-slik-magenta rounded-full flex items-center justify-center text-white text-3xl font-bold border-4 border-white shadow-lg group-hover:scale-105 transition-transform">
                        {barber.firstName?.charAt(0) || 'B'}
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="bg-white/90 text-slik-purple font-bold px-3 py-1 rounded-full text-sm shadow-lg">
                        View Profile
                      </span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slik-navy group-hover:text-slik-purple transition-colors">{getBarberName(barber)}</h3>
                  <div className="flex items-center justify-center gap-1 text-yellow-500 mt-1">
                    <Star className="w-4 h-4 fill-yellow-500" />
                    <span className="text-sm font-medium">{barber.rating?.toFixed(1) || '4.9'}</span>
                  </div>
                  {barber.specialties && barber.specialties.length > 0 && (
                    <p className="text-gray-500 text-sm mt-2">{barber.specialties.slice(0, 2).join(' • ')}</p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-20 bg-slik-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">What Our Clients Say</h2>
            <div className="flex items-center justify-center gap-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map(i => (
                  <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <span className="text-white/80">{config.stats.rating} out of 5 based on {config.stats.reviewCount} reviews</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {config.testimonials.map((review, idx) => (
              <div
                key={review.id}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map(i => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-white text-lg mb-4">"{review.text}"</p>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-white">{review.name}</span>
                  <span className="text-white/60 text-sm">{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slik-navy mb-4">Our Space</h2>
            <p className="text-gray-600">Step into a modern, comfortable atmosphere</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {config.galleryImages?.map((img, idx) => (
              <div
                key={idx}
                className="aspect-[4/3] rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover transition-all"
              >
                <img
                  src={img}
                  alt={`Shop gallery ${idx + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slik-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Sparkles className="w-12 h-12 text-slik-purple mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready for Your New Look?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Book your appointment online in seconds. No account required for first-time visitors.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/book"
              className="inline-flex items-center gap-2 bg-slik-purple text-white px-8 py-4 rounded-button font-bold text-lg shadow-button hover:opacity-90 transition-all"
            >
              <Calendar className="w-5 h-5" />
              Book Online Now
            </Link>
            <span className="text-gray-500">or</span>
            <a
              href={`tel:${config.shopInfo.phone}`}
              className="inline-flex items-center gap-2 text-white hover:text-slik-purple transition-colors"
            >
              <Phone className="w-5 h-5" />
              {config.shopInfo.phone}
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section — SEO/AEO (Jose 2026-09-16). Visible answers mirror the FAQPage JSON-LD in
          index.html; voice-search phrasing so Google/AI can lift them. Keyworded: Florham Park, fade, walk-in. */}
      <section className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slik-navy mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-bold text-slik-navy mb-1">Where can I get a fade in Florham Park, NJ?</h3>
              <p className="text-gray-600">SLIK Barber Lounge at 184 Columbia Turnpike, Florham Park specializes in skin fades, taper fades, and all modern hairstyles. Book online.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-slik-navy mb-1">Does SLIK Barber Lounge take walk-ins?</h3>
              <p className="text-gray-600">Walk-ins are welcome when a barber is available, but we often book out about a week in advance — booking online guarantees your time and barber.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-slik-navy mb-1">What services does SLIK Barber Lounge offer?</h3>
              <p className="text-gray-600">Skin fades, tapers, classic and scissor cuts, and all modern hairstyles — plus crisp line-ups, hot-towel and straight-razor shaves, beard shaping, and our signature Whiskey Works (haircut, shave &amp; barber facial). Reserve your barber online.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-slik-navy mb-1">How far in advance should I book?</h3>
              <p className="text-gray-600">We book out roughly a week ahead — reserve a few days early to get your preferred barber and time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact/Footer Section */}
      <footer id="contact" className="bg-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <ShopLogo className="w-10 h-10 object-contain rounded-xl bg-white" />
                <ShopName nameClassName="text-xl font-bold text-white" taglineClassName="text-xs text-gray-400" />
              </div>
              <p className="text-gray-400 mb-4">{config.shopInfo.description}</p>
              <div className="flex gap-4">
                <a href={config.socialLinks?.instagram || "https://www.instagram.com/slik_barber_lounge"} target="_blank" rel="noopener noreferrer" aria-label="SLIK Barber Lounge on Instagram" className="text-gray-400 hover:text-slik-purple transition-colors">
                  <Instagram className="w-6 h-6" />
                </a>
                <a href={config.socialLinks?.facebook || "https://www.facebook.com/SLIKBarberLounge/"} target="_blank" rel="noopener noreferrer" aria-label="SLIK Barber Lounge on Facebook" className="text-gray-400 hover:text-slik-purple transition-colors">
                  <Facebook className="w-6 h-6" />
                </a>
              </div>
            </div>

            {/* Hours */}
            <div>
              <h3 className="text-white font-bold mb-4">Hours</h3>
              <div className="space-y-2 text-gray-400">
                {hoursLines.length > 0 ? (
                  hoursLines.map((h) => (
                    <p key={h.label}>{h.label}: {h.value}</p>
                  ))
                ) : (
                  <p>&nbsp;</p>
                )}
              </div>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-white font-bold mb-4">Contact</h3>
              <div className="space-y-3">
                <a href={`tel:${config.shopInfo.phone}`} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
                  <Phone className="w-5 h-5" />
                  {config.shopInfo.phone}
                </a>
                <div className="flex items-start gap-2 text-gray-400">
                  <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{config.shopInfo.address}</span>
                </div>
                {/* Google Map embed (keyless) — extracted from the WP site, tightened to street level.
                    Jose 2026-09-19: footer lower-right, by the address. */}
                <div className="mt-1 rounded-xl overflow-hidden border border-gray-800">
                  <iframe
                    title="SLIK Barber Lounge location map"
                    src="https://maps.google.com/maps?q=SLIK%20Barber%20Lounge%2C%20184%20Columbia%20Turnpike%2C%20Florham%20Park%2C%20NJ%2007932&t=m&z=15&output=embed"
                    width="100%"
                    height="170"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <a
                    href="https://maps.google.com/maps?q=SLIK+Barber+Lounge,184+Columbia+Turnpike,Florham+Park,NJ+07932"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center bg-gray-800 text-gray-300 hover:text-white text-sm py-2 transition-colors"
                  >
                    Get Directions →
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {config.shopInfo.name}. Powered by SLIK POS.
            </p>
            <div className="flex items-center gap-5">
              {/* Privacy Policy link — discoverable from the main site for A2P 10DLC / carrier review
                  (Jose 2026-09-15). Full page load → served by the static /privacy.html (crawler-safe). */}
              <a
                href="/privacy"
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                Privacy &amp; SMS Policy
              </a>
              <Link
                to="/login"
                className="text-slik-purple hover:text-slik-magenta transition-colors text-sm"
              >
                Already a client? Login to your portal
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <ServiceModal
        service={selectedService}
        onClose={() => setSelectedService(null)}
        onBook={handleBookService}
      />
      <BarberModal
        barber={selectedBarber}
        onClose={() => setSelectedBarber(null)}
        onBook={handleBookBarber}
      />
    </div>
  );
};

export default LandingPage;
